import {
  checkSubmissionsExistBulk,
  searchJobs,
  searchQuickSubmitJobs,
  tagCandidatesToJob,
} from '../../../../services/popupApi';
import { openFormDecision, openFormNotice } from '../../../form/formDecisionDialog';
import { createWorkflowInstance } from '../../../../services/workflowApi';

const STAGE_ID_BY_KEY = {
  pipeline: 'pipeline',
  goodfited: 'goodfited',
  contacted: 'contacted',
  submitted: 'submitted',
  shortlisted: 'shortlisted',
  interviewinprogress: 'interviewInprogress',
  selected: 'selected',
};
const ALREADY_SUBMITTED_LABEL = 'Already submitted';

function pickValue(record, ...paths) {
  for (const path of paths) {
    const value = String(path).split('.').reduce((current, part) => current?.[part], record);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function normalizeStageId(stage) {
  const raw = String(stage ?? '').trim();
  if (!raw) return '';
  return STAGE_ID_BY_KEY[raw.toLowerCase()] ?? raw;
}

function candidateIdOf(record, fallbackCandidateId = '') {
  return String(
    pickValue(
      record,
      'candidateId',
      'candidate_id',
      'applicantId',
      'applicantID',
      'applicantReferenceId',
      'applicantRefrenceId',
      '_id',
      'id',
    ) ?? fallbackCandidateId ?? '',
  );
}

function workflowCandidateIdOf(record) {
  return String(
    pickValue(
      record,
      '_id',
      'id',
      'candidateId',
      'candidate_id',
      'applicantId',
      'applicantID',
    ) ?? '',
  );
}

function markSubmitted(option, submissionId, submittedAt) {
  const jobTitle = String(option.jobTitle ?? '').trim();
  return {
    ...option,
    jobTitle: jobTitle ? `${jobTitle} (${ALREADY_SUBMITTED_LABEL})` : `(${ALREADY_SUBMITTED_LABEL})`,
    disabled: true,
    title: ALREADY_SUBMITTED_LABEL,
    alreadySubmitted: true,
    existingSubmissionId: submissionId || '',
    ...(submittedAt ? { submittedAt } : {}),
  };
}

function formatBlockedLabel(label, note) {
  const status = String(note ?? '').trim();
  if (!status) return label;
  if (/already submitted/i.test(status)) return `${label} (${ALREADY_SUBMITTED_LABEL})`;
  if (/fulfilled/i.test(status)) return `${label} (Fullfilled)`;
  return `${label} (${status})`;
}

function normalizeJobRow(item) {
  const jobReferenceId = String(item.jobReferenceId ?? item.clientJobReferenceId ?? '').trim();
  const jobTitle = String(item.jobTitle ?? '').trim();
  return {
    ...item,
    jobReferenceId,
    jobTitle,
  };
}

function selectedCandidateIds(context) {
  return selectedCandidates(context).map((candidate) => candidate.id);
}

function candidateNameOf(record, fallbackCandidateId = '') {
  return (
    pickValue(record, 'candidateName', 'candidate_name', 'fullName', 'name')
    ?? [pickValue(record, 'firstName', 'first_name'), pickValue(record, 'lastName', 'last_name')]
      .filter(Boolean)
      .join(' ')
    ?? fallbackCandidateId
  ) || 'Unknown candidate';
}

function selectedCandidates(context) {
  const selectedIds = Array.isArray(context?.selectedIds) ? context.selectedIds : [];
  const selectedRecords = Array.isArray(context?.selectedRecords) ? context.selectedRecords : [];
  const candidates = [];
  const seen = new Set();
  const total = Math.max(selectedIds.length, selectedRecords.length);

  for (let index = 0; index < total; index += 1) {
    const record = selectedRecords[index] ?? {};
    const fallbackCandidateId = selectedIds[index] ? String(selectedIds[index]) : '';
    const candidateId = candidateIdOf(record, fallbackCandidateId);
    if (!candidateId || seen.has(candidateId)) continue;
    seen.add(candidateId);
    candidates.push({
      id: candidateId,
      name: candidateNameOf(record, fallbackCandidateId),
      record,
      selectedId: fallbackCandidateId || candidateId,
    });
  }

  return candidates;
}

function selectedJobIdsOf(values, context) {
  if (Array.isArray(values?.jobId)) return values.jobId.map(String).filter(Boolean);
  if (values?.jobId) return [String(values.jobId)];
  if (Array.isArray(context?.jobId)) return context.jobId.map(String).filter(Boolean);
  if (context?.jobId) return [String(context.jobId)];
  return [];
}

function extractJobTitle(job, fallbackJobId = '') {
  const directTitle = String(
    job?.jobTitle
    ?? job?._raw?.jobTitle
    ?? '',
  ).trim();
  if (directTitle) return directTitle;

  const label = String(job?.label ?? job?._raw?.label ?? '').trim();
  if (label.includes(' - ')) return label.split(' - ').slice(1).join(' - ').trim();
  return label || String(fallbackJobId ?? '');
}

function jobLabelOf(job, fallbackJobId = '') {
  return extractJobTitle(job, fallbackJobId);
}

function selectedJobs(values, context, submitPayload) {
  const selectedJobIds = [...new Set(selectedJobIdsOf(values, context))];
  const jobOptions = Array.isArray(submitPayload?.dynamicOptions?.jobId) ? submitPayload.dynamicOptions.jobId : [];
  const optionsById = new Map(jobOptions.map((option) => [String(option?._id ?? option?.value ?? ''), option]));

  return selectedJobIds.map((jobId) => {
    const job = optionsById.get(String(jobId)) ?? {};
    return {
      id: String(jobId),
      label: jobLabelOf(job, jobId),
      record: job,
    };
  });
}

function summarizeBlockedJobs(plans) {
  return plans
    .filter((plan) => plan.blockedCandidates.length > 0)
    .map((plan) => `${plan.job.label} - ${plan.blockedCandidates.map((candidate) => candidate.name).join(', ')}`)
    .join(' | ');
}

function summarizeAllowedJobs(plans) {
  return plans
    .filter((plan) => plan.allowedCandidates.length > 0)
    .map((plan) => `${plan.job.label} - ${plan.allowedCandidates.map((candidate) => candidate.name).join(', ')}`)
    .join(' | ');
}

function buildCandidateDetails(record, fallbackCandidateId) {
  const candidateId = workflowCandidateIdOf(record);

  if (!candidateId) return null;

  const candidateName = candidateNameOf(record, fallbackCandidateId) || undefined;

  const candidateDetails = {
    candidateId: String(candidateId),
    candidateName,
    phone: pickValue(record, 'phone', 'phoneNumber', 'phone_number', 'mobile', 'mobileNumber', 'mobile_number'),
    source: pickValue(record, 'source', 'sourceType', 'candidateSource', 'profileSource'),
    submittedAt: pickValue(record, 'submittedAt', 'lastSubmittedAt', 'updatedAt'),
    remarks: pickValue(record, 'remarks', 'comment', 'comments', 'notes'),
    assignedRecruiter: pickValue(record, 'assignedRecruiter', 'recruiterName', 'ownerName', 'assignedToName'),
    screeningStatus: pickValue(record, 'screeningStatus', 'screening.status', 'screeningResult'),
    screeningScore: pickValue(record, 'screeningScore', 'screening.score', 'score'),
    assignedDate: pickValue(record, 'assignedDate', 'assignedAt', 'createdAt'),
  };

  return Object.fromEntries(Object.entries(candidateDetails).filter(([, value]) => value !== undefined && value !== null && value !== ''));
}

export const api = {
  searchOptions: async (field, search, context) => {
    if (field.searchFetchKey === 'tagToJobJobList') {
      const candidateIds = selectedCandidateIds(context);
      const candidateId = candidateIds.length === 1 ? candidateIds[0] : '';

      let list;
      let flaggedByGateway = false;
      if (candidateId) {
        try {
          list = await searchQuickSubmitJobs({ searchQuery: search, candidateId });
          flaggedByGateway = true;
        } catch {
          flaggedByGateway = false;
        }
      }

      if (!Array.isArray(list)) list = await searchJobs(search);

      const options = list.map(normalizeJobRow);
      if (flaggedByGateway) {
        return options.map((opt) => {
          if (opt?.isSubmitted || opt?.alreadySubmitted) {
            return markSubmitted(
              opt,
              opt?.jobSubmissionId || opt?.submissionId || '',
              opt?.submittedAt || '',
            );
          }
          if (opt?.disable) {
            const note = String(opt?.message ?? '').trim();
            return {
              ...opt,
              jobTitle: opt.jobTitle ? formatBlockedLabel(opt.jobTitle, note) : `(${note})`,
              disabled: true,
              optionNote: note,
              title: note,
            };
          }
          return opt;
        });
      }

      if (!candidateId) return options;

      const submitted = await checkSubmissionsExistBulk({
        applicantId: candidateId,
        jobIds: options.map((opt) => opt._id).filter(Boolean),
      }).catch(() => new Map());

      return options.map((opt) => {
        const hit = submitted.get(String(opt._id));
        return hit ? markSubmitted(opt, hit.submissionId, hit.submittedAt) : opt;
      });
    }
    return [];
  },
  submit: async (values, context, submitPayload) => {
    const selectedHiringStage = normalizeStageId(values.hiringStage ?? context.hiringStage);
    const candidates = selectedCandidates(context);
    const jobs = selectedJobs(values, context, submitPayload);
    if (!jobs.length || !candidates.length) return { cancelled: true };

    const plans = [];
    for (const job of jobs) {
      const duplicateMap = await checkSubmissionsExistBulk({
        jobId: job.id,
        applicantIds: candidates.map((candidate) => candidate.id),
      }).catch(() => new Map());

      plans.push({
        job,
        blockedCandidates: candidates.filter((candidate) => duplicateMap.has(String(candidate.id))),
        allowedCandidates: candidates.filter((candidate) => !duplicateMap.has(String(candidate.id))),
      });
    }

    const blockedSummary = summarizeBlockedJobs(plans);
    const allowedSummary = summarizeAllowedJobs(plans);
    const executablePlans = plans.filter((plan) => plan.allowedCandidates.length > 0);

    if (!executablePlans.length) {
      await openFormNotice({
        tone: 'duplicate',
        title: 'Selected candidates are already submitted',
        body: 'Every selected candidate is already submitted to the selected jobs. Choose different jobs to continue.',
        facts: [
          { label: 'Blocked combinations', value: blockedSummary },
        ],
        okText: 'Close',
      });
      return { cancelled: true };
    }

    if (plans.some((plan) => plan.blockedCandidates.length > 0)) {
      const proceed = await openFormDecision({
        tone: 'duplicate',
        title: 'Some candidate-job combinations are already submitted',
        body: 'Those combinations will be skipped. Continue tagging the remaining valid combinations?',
        facts: [
          { label: 'Will be skipped', value: blockedSummary },
          { label: 'Will be tagged', value: allowedSummary },
        ],
        okText: 'Tag remaining',
        cancelText: 'Cancel',
        footnote: 'Already submitted combinations will be left unchanged.',
      });
      if (!proceed) return { cancelled: true };
    }

    for (const plan of executablePlans) {
      for (const candidate of plan.allowedCandidates) {
        const candidateDetails = buildCandidateDetails(candidate.record ?? {}, candidate.selectedId);
        await createWorkflowInstance({
          jobId: plan.job.id,
          module: 'jobs',
          currentStage: selectedHiringStage,
          ...(candidateDetails ? { candidateDetails } : {}),
        });
      }

      await tagCandidatesToJob({
        jobId: plan.job.id,
        candidateIds: plan.allowedCandidates.map((candidate) => candidate.selectedId || candidate.id),
        hiringStage: selectedHiringStage,
      });
    }
  },
};
